
import { ExternalLink } from 'lucide-react';

export interface Product {
  id: string;
  priority: 'P1' | 'P2';
  category: string;
  title: string;
  tagline: string;
  description: string;
  tags: string[];
  site?: string;
  whatsapp?: string;
  featured?: boolean;
}

const ProductCard = ({ product, index }: { product: Product; index: number }) => {
  const isLight = index % 2 === 1;

  const bg = isLight ? '#F2EDE3' : '#0B0B0B';
  const textMain = isLight ? '#0B0B0B' : '#F2EDE3';
  const textSub = isLight ? '#1A1916' : '#8C8377';
  const borderColor = isLight ? 'rgba(11,11,11,0.12)' : 'rgba(242,237,227,0.1)';
  const tagBg = isLight ? 'rgba(11,11,11,0.07)' : 'rgba(242,237,227,0.06)';
  const tagColor = isLight ? '#1A1916' : '#8C8377';

  return (
    <article
      style={{
        backgroundColor: bg,
        borderTop: `2px solid ${product.featured ? '#FF4500' : borderColor}`,
        padding: '2.5rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '1.25rem',
      }}
    >
      {/* Top row: priority chip + category */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <span
          style={{
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            fontSize: '0.6rem',
            letterSpacing: '0.12em',
            textTransform: 'uppercase' as const,
            color: product.priority === 'P1' ? '#0B0B0B' : '#FF4500',
            backgroundColor: product.priority === 'P1' ? '#FF4500' : 'transparent',
            border: product.priority === 'P1' ? 'none' : '1px solid rgba(255,69,0,0.5)',
            padding: '3px 8px',
          }}
        >
          {product.priority}
        </span>
        <span
          style={{
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            fontSize: '0.6rem',
            letterSpacing: '0.1em',
            textTransform: 'uppercase' as const,
            color: '#FF4500',
          }}
        >
          {product.category}
        </span>
      </div>

      {/* Title */}
      <h3
        style={{
          fontFamily: "'Anton', 'Archivo Black', sans-serif",
          fontSize: 'clamp(1.6rem, 3.5vw, 2.2rem)',
          lineHeight: 0.95,
          letterSpacing: '-0.01em',
          textTransform: 'uppercase' as const,
          color: textMain,
          margin: 0,
        }}
      >
        {product.title}
      </h3>

      {/* Tagline */}
      <p
        style={{
          fontFamily: "'Instrument Serif', 'Times New Roman', serif",
          fontStyle: 'italic',
          fontSize: 'clamp(1rem, 2vw, 1.15rem)',
          lineHeight: 1.5,
          color: textMain,
          margin: 0,
        }}
      >
        {product.tagline}
      </p>

      {/* Description */}
      <p
        style={{
          fontFamily: "'Space Grotesk', system-ui, sans-serif",
          fontSize: '0.95rem',
          lineHeight: 1.65,
          color: textSub,
          margin: 0,
          flexGrow: 1,
        }}
      >
        {product.description}
      </p>

      {/* Tags */}
      <div style={{ display: 'flex', flexWrap: 'wrap' as const, gap: '0.4rem' }}>
        {product.tags.map((tag) => (
          <span
            key={tag}
            style={{
              fontFamily: "'JetBrains Mono', ui-monospace, monospace",
              fontSize: '0.6rem',
              letterSpacing: '0.08em',
              textTransform: 'uppercase' as const,
              color: tagColor,
              backgroundColor: tagBg,
              padding: '3px 8px',
            }}
          >
            {tag}
          </span>
        ))}
      </div>

      {/* CTAs */}
      {(product.site || product.whatsapp) && (
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' as const, marginTop: '0.25rem' }}>
          {product.site && (
            <a
              href={product.site}
              target={product.site.startsWith('http') ? '_blank' : undefined}
              rel={product.site.startsWith('http') ? 'noopener noreferrer' : undefined}
              style={{
                fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                fontSize: '0.7rem',
                letterSpacing: '0.08em',
                textTransform: 'uppercase' as const,
                color: '#0B0B0B',
                backgroundColor: '#FF4500',
                padding: '0.5rem 1.25rem',
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.4rem',
              }}
            >
              Ver projeto <ExternalLink size={11} />
            </a>
          )}
          {product.whatsapp && (
            <a
              href={product.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                fontSize: '0.7rem',
                letterSpacing: '0.08em',
                textTransform: 'uppercase' as const,
                color: isLight ? '#0B0B0B' : '#F2EDE3',
                border: `1px solid ${isLight ? 'rgba(11,11,11,0.3)' : 'rgba(242,237,227,0.25)'}`,
                padding: '0.5rem 1.25rem',
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.4rem',
              }}
            >
              WhatsApp
            </a>
          )}
        </div>
      )}
    </article>
  );
};

export default ProductCard;
